import { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import S25FLaunchCountdown from '../S25FLaunchCountdown';
import DonationForm from '../DonationForm';
import {
  fetchDonationConfig,
  PublicDonationConfig,
  DonationFrequency,
} from '../../api/donations';
import '../../styles/donate.css';

type Load =
  | { status: 'loading' }
  | { status: 'ready'; config: PublicDonationConfig }
  | { status: 'closed' } // reachable server, but this tenant isn't taking gifts
  | { status: 'error' };

/**
 * The `DonateForm` registered section type (see tenant_site/section_types.py,
 * tenant_site/system_pages.py's DonatePage) — the locked, non-deletable
 * second section on the Donate System Page. No admin-editable `data`: this
 * embeds the real gift flow, gated on the Section 25F launch date the same
 * way the standalone `/donate` route used to (see git history —
 * `pages/Donate.tsx` was a hardcoded route before this moved into the CMS).
 *
 * Charges are direct, so the browser's Stripe.js must be initialized against
 * the tenant's connected account: `loadStripe(publishableKey, { stripeAccount })`.
 * The Payment Element runs in Stripe's deferred-intent mode — we tell it the
 * amount and currency up front and create the PaymentIntent (or Subscription)
 * only when the donor submits, so a single Element serves any amount.
 */
export default function DonateFormSection() {
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchDonationConfig(controller.signal)
      .then((config) =>
        setLoad(config.is_ready ? { status: 'ready', config } : { status: 'closed' }))
      .catch(() => {
        if (!controller.signal.aborted) setLoad({ status: 'error' });
      });
    return () => controller.abort();
  }, []);

  return (
    <section className="section donate">
      <div className="wrap donate-wrap">
        <S25FLaunchCountdown
          title="Donations open January 1, 2027"
          description="Section 25F creates a federal tax credit for gifts to approved scholarship organizations, starting tax year 2027. Come back then to give — and claim your credit."
        >
          <div className="head">
            <div className="eyebrow">Support the mission</div>
            <h2>Make a <em>gift</em></h2>
          </div>
          {load.status === 'loading' && (
            <p data-testid="donate-loading">Loading…</p>
          )}
          {load.status === 'error' && (
            <p data-testid="donate-error">Donations are unavailable right now.</p>
          )}
          {load.status === 'closed' && (
            <p data-testid="donate-closed">
              This organization is not currently accepting online donations.
            </p>
          )}
          {load.status === 'ready' && <DonateReady config={load.config} />}
        </S25FLaunchCountdown>
      </div>
    </section>
  );
}

/**
 * The two Section 25F federal tax-credit caps. A donor gives up to their cap and
 * recovers it dollar-for-dollar. These anchor the whole donate flow and are the
 * same for every tenant — they're set by federal law, not tenant configuration —
 * so they live here as product constants rather than coming from the API.
 *
 * A monthly gift spreads the annual cap over 12 payments, rounded to a whole
 * dollar (so 12× lands a few dollars above the cap: $142/mo → $1,704/yr).
 */
const CREDIT_LEVELS = [
  { key: 'individual', label: 'Individual', annualCents: 170000 },
  { key: 'couple', label: 'Couple / joint filers', annualCents: 340000 },
] as const;

type LevelKey = (typeof CREDIT_LEVELS)[number]['key'];
type Selection = LevelKey | 'other';

function monthlyCents(annualCents: number): number {
  return Math.round(annualCents / 12 / 100) * 100;
}

/** The charged amount for a level at a given frequency. */
function levelAmount(annualCents: number, frequency: DonationFrequency): number {
  return frequency === 'monthly' ? monthlyCents(annualCents) : annualCents;
}

function DonateReady({ config }: { config: PublicDonationConfig }) {
  const [frequency, setFrequency] = useState<DonationFrequency>('one_time');
  const [selection, setSelection] = useState<Selection>(CREDIT_LEVELS[0].key);
  // Only meaningful while `selection === 'other'`; null means the field is empty.
  const [customCents, setCustomCents] = useState<number | null>(null);

  // The amount to charge is derived from the selection and frequency, so
  // switching frequency re-prices the chosen level instead of stranding a stale
  // annual amount on a monthly gift.
  const amountCents = useMemo(() => {
    if (selection === 'other') return customCents ?? 0;
    const level = CREDIT_LEVELS.find((l) => l.key === selection)!;
    return levelAmount(level.annualCents, frequency);
  }, [selection, customCents, frequency]);

  // Init Stripe.js once for this connected account (direct charges).
  const stripePromise: Promise<Stripe | null> = useMemo(
    () => loadStripe(config.publishable_key, { stripeAccount: config.stripe_account_id }),
    [config.publishable_key, config.stripe_account_id],
  );

  const elementsOptions = {
    mode: (frequency === 'monthly' ? 'subscription' : 'payment') as 'subscription' | 'payment',
    amount: Math.max(amountCents, config.min_amount_cents),
    currency: config.currency,
    appearance: { theme: 'flat' as const },
  };

  return (
    <div className="donate-card">
      <FrequencyToggle frequency={frequency} onChange={setFrequency} />
      <AmountPicker
        frequency={frequency}
        selection={selection}
        customCents={customCents}
        minAmountCents={config.min_amount_cents}
        onSelectLevel={(key) => {
          setSelection(key);
          setCustomCents(null);
        }}
        onCustomChange={(cents) => {
          setSelection('other');
          setCustomCents(cents);
        }}
      />
      {/* Remount Stripe Elements when the frequency (and thus mode) changes —
          a live Elements instance can't switch mode. */}
      <Elements key={frequency} stripe={stripePromise} options={elementsOptions}>
        <DonationForm
          amountCents={amountCents}
          frequency={frequency}
          minAmountCents={config.min_amount_cents}
          currency={config.currency}
        />
      </Elements>
    </div>
  );
}

function FrequencyToggle({
  frequency,
  onChange,
}: {
  frequency: DonationFrequency;
  onChange: (f: DonationFrequency) => void;
}) {
  const options: { value: DonationFrequency; label: string }[] = [
    { value: 'one_time', label: 'One-time' },
    { value: 'monthly', label: 'Monthly' },
  ];
  return (
    <div className="donate-frequency" role="group" aria-label="Donation frequency">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`donate-freq-btn${frequency === option.value ? ' is-active' : ''}`}
          aria-pressed={frequency === option.value}
          onClick={() => onChange(option.value)}
          data-testid={`donate-frequency-${option.value}-button`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function AmountPicker({
  frequency,
  selection,
  customCents,
  minAmountCents,
  onSelectLevel,
  onCustomChange,
}: {
  frequency: DonationFrequency;
  selection: Selection;
  customCents: number | null;
  minAmountCents: number;
  onSelectLevel: (key: LevelKey) => void;
  onCustomChange: (cents: number | null) => void;
}) {
  const isMonthly = frequency === 'monthly';
  const [custom, setCustom] = useState('');

  // Clear the visible "Other" text once a preset takes over the selection, so a
  // stale number doesn't linger in the field the donor didn't choose.
  const wasOther = useRef(false);
  useEffect(() => {
    if (selection === 'other') wasOther.current = true;
    else if (wasOther.current) {
      wasOther.current = false;
      setCustom('');
    }
  }, [selection]);

  return (
    <div className="donate-amounts">
      <div className="donate-amount-grid">
        {CREDIT_LEVELS.map((level) => {
          const cents = levelAmount(level.annualCents, frequency);
          const active = selection === level.key;
          return (
            <button
              key={level.key}
              type="button"
              className={`donate-amount-btn${active ? ' is-active' : ''}`}
              aria-pressed={active}
              onClick={() => onSelectLevel(level.key)}
              data-testid={`donate-amount-${level.key}-button`}
            >
              <span className="donate-amount-value">
                {formatDollars(cents)}
                {isMonthly && <span className="donate-amount-per">/mo</span>}
              </span>
              <span className="donate-amount-note">
                {/* On monthly, show the arithmetic back to the credit cap so the
                    donor can see the gift annualizes to their full credit. */}
                {isMonthly
                  ? `${level.label} · ${formatDollars(level.annualCents)}/yr`
                  : level.label}
              </span>
            </button>
          );
        })}
      </div>
      <label
        className={`donate-custom${selection === 'other' ? ' is-active' : ''}`}
      >
        <span className="donate-custom-prefix">$</span>
        <input
          type="number"
          min={minAmountCents / 100}
          step="1"
          inputMode="decimal"
          placeholder="Other amount"
          value={custom}
          onChange={(event) => {
            const value = event.target.value;
            setCustom(value);
            const dollars = parseFloat(value);
            onCustomChange(Number.isNaN(dollars) ? null : Math.round(dollars * 100));
          }}
          data-testid="donate-custom-amount-input"
        />
        {isMonthly && <span className="donate-custom-suffix">/mo</span>}
      </label>
    </div>
  );
}

function formatDollars(cents: number): string {
  const dollars = cents / 100;
  return `$${Number.isInteger(dollars) ? dollars.toLocaleString('en-US') : dollars.toFixed(2)}`;
}
